import { useEffect } from 'react';

const SITE = 'https://qasc.it';

// Upsert a <meta> tag by identifying attribute (name|property)
function setMeta(attr, key, content) {
  if (content == null || content === '') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Per-route SEO meta. Imperatively manages document head (title, description,
 * canonical, Open Graph, Twitter) plus optional JSON-LD. index.html carries no
 * variable meta, so this is the single source of truth — no duplicate tags.
 *
 * react-helmet-async@2 is broken (renders nothing), hence the manual approach.
 */
function Seo({
  title,
  description,
  canonical = `${SITE}/`,
  image = `${SITE}/assets/images/logo-logo.png`,
  type = 'website',
  robots,
  published,
  author,
  section,
  jsonLd = [],
}) {
  const jsonLdKey = JSON.stringify(jsonLd);
  useEffect(() => {
    if (title) document.title = title;
    setMeta('name', 'description', description);
    setLink('canonical', canonical);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:site_name', 'QAS Consulting');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', image);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image);
    setMeta('property', 'article:published_time', published);
    setMeta('property', 'article:author', author);
    setMeta('property', 'article:section', section);

    // robots: only set when explicitly requested (e.g. noindex on 404)
    const robotsEl = robots
      ? (() => {
          let el = document.head.querySelector('meta[name="robots"]');
          if (!el) {
            el = document.createElement('meta');
            el.setAttribute('name', 'robots');
            document.head.appendChild(el);
          }
          el.setAttribute('content', robots);
          return el;
        })()
      : null;

    // Remove any prerendered/previous-route JSON-LD so we never duplicate it
    document.head.querySelectorAll('script[data-seo="1"]').forEach((s) => s.remove());

    // JSON-LD — tagged so we can remove on unmount / route change
    // (parsed from jsonLdKey so the effect deps stay value-based; the jsonLd
    // array prop is a fresh reference every render)
    const scripts = JSON.parse(jsonLdKey).map((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo', '1');
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      scripts.forEach((s) => s.remove());
      if (robotsEl) robotsEl.remove();
    };
  }, [title, description, canonical, image, type, robots, published, author, section, jsonLdKey]);

  return null;
}

export default Seo;
